import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import cloudRaymarchFrag from '../../glsl/cloud_raymarch_frag.glsl';
import cloudCompositeFrag from '../../glsl/cloud_composite_frag.glsl';
import cloudBlurFrag from '../../glsl/cloud_blur_frag.glsl';
import { CLOUDS } from '../core/SimConfig.js';

// ── CloudLayer ────────────────────────────────────────────────────────────────
//
// A single volumetric cloud deck, implemented as an EffectComposer pass inserted
// straight after the RenderPass. It raymarches a horizontal slab of FBM density
// at a fraction of screen resolution, lights it with the scenario sun plus a
// hemispheric sky-tint ambient (the same HDRI approximation the particle/smoke
// systems use), fogs it with the scene fog, and clips the march against scene
// depth (reconstructed from the logarithmic depth buffer) so terrain and objects
// occlude it. The low-res result is bicubically upscaled and composited over the
// beauty pass.
//
// The pass reads the scene colour + depth from the composer's `readBuffer` (the
// RenderPass renders into it with `needsSwap = false`), so App only has to attach
// a DepthTexture to the composer's render targets.

const CLOUD_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export default class CloudLayer extends Pass {
  /**
   * @param {{ camera: THREE.PerspectiveCamera }} opts
   */
  constructor({ camera }) {
    super();
    this.camera = camera;
    this.needsSwap = true;

    // Stays off until the first configure() so nothing renders over the loading
    // screen / an unconfigured scene.
    this.enabled = false;

    // The player's "Cloud quality: Off" choice. Held apart from CLOUDS.enabled
    // (the build switch) so the two compose, and so turning it back on restores
    // the deck fully — including the terrain cast, whose shader patch Terrain
    // injects once at construction off the build switch alone.
    this._userEnabled = true;

    this._resolutionScale = CLOUDS.resolutionScale;
    this._fullWidth = 1;
    this._fullHeight = 1;

    // Scratch colour objects for sRGB→linear conversions (Color stores linear).
    this._col = new THREE.Color();
    this._sunDir = new THREE.Vector3();

    this._noise = CloudLayer.buildNoiseTexture();
    this._blueNoise = CloudLayer.buildBlueNoiseTexture();

    // ── Terrain cloud-shadow uniforms ────────────────────────────────────────
    // A shared { value } block handed to the Terrain materials so the deck casts
    // a soft shadow on the ground. It mirrors the beauty-pass shape params
    // (coverage/density/altitude/drift/sun) so the shadow tracks the visible
    // clouds; the same packed noise texture drives both. configure() refreshes
    // the look params, update() advances the clock. uCloudShadowStrength gates
    // the whole effect — 0 when clouds or the sun are off. Kept even when
    // CLOUDS.shadow is disabled (App just never injects it into the terrain).
    this.shadowUniforms = {
      uCloudShadowStrength:  { value: 0 },
      uCloudShadowNoise:     { value: this._noise },
      uCloudShadowCoverage:  { value: 0.5 },
      uCloudShadowDensity:   { value: 0.05 },
      uCloudShadowEdge:      { value: 0.35 },
      uCloudShadowNoiseScale:{ value: CLOUDS.noiseScale },
      uCloudShadowWindDir:   { value: new THREE.Vector3(...CLOUDS.windDir).normalize() },
      uCloudShadowWindSpeed: { value: CLOUDS.windSpeed },
      uCloudShadowTime:      { value: 0 },
      uCloudShadowBase:      { value: CLOUDS.altitude.base },
      uCloudShadowTop:       { value: CLOUDS.altitude.top },
      uCloudShadowSunDir:    { value: new THREE.Vector3(0, 1, 0) },
    };

    // Low-res raymarch targets — HDR (HalfFloat) so bright sunlit tops survive the
    // bloom/tone-map pipeline; linear filtered for the bicubic upscale and the
    // history reprojection sample. Two of them, ping-ponged each frame: the shader
    // reads the previous frame's accumulation and writes this frame's.
    const makeTarget = () => new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      depthBuffer: false,
      stencilBuffer: false,
      magFilter: THREE.LinearFilter,
      minFilter: THREE.LinearFilter,
    });
    this._prev = makeTarget();   // previous frame's accumulated buffer (read)
    this._curr = makeTarget();   // this frame's target (write)

    // Temporal accumulation state.
    this._historyValid = false;          // false until the first frame is banked
    this._jitter = 0;                    // per-frame ray-start offset, [0,1)
    this._prevViewProj = new THREE.Matrix4(); // last frame's world -> clip

    // CLOUD_WISPS gates the fine-detail erosion path: left undefined it compiles
    // out of the shader entirely, so `wisp.enabled: false` costs literally
    // nothing rather than a per-sample branch.
    const defines = {
      CLOUD_STEPS: `${Math.max(1, CLOUDS.maxSteps | 0)}`,
      CLOUD_OCTAVES: `${Math.max(1, CLOUDS.octaves | 0)}`,
    };
    if (CLOUDS.wisp.enabled) {
      defines.CLOUD_WISPS = '';
      defines.CLOUD_WISP_OCTAVES = `${Math.max(1, CLOUDS.wisp.octaves | 0)}`;
    }

    this._raymarchMat = new THREE.ShaderMaterial({
      name: 'CloudRaymarch',
      defines,
      uniforms: {
        uSceneDepth: { value: null },
        uNoise: { value: this._noise },
        uBlueNoise: { value: this._blueNoise },
        uPrevHistory: { value: null },
        uPrevViewProj: { value: new THREE.Matrix4() },
        uHistoryValid: { value: 0 },
        uBlend: { value: CLOUDS.temporalBlend },
        uJitter: { value: 0 },
        uCameraPos: { value: new THREE.Vector3() },
        uInvProjection: { value: new THREE.Matrix4() },
        uInvView: { value: new THREE.Matrix4() },
        uViewMatrix: { value: new THREE.Matrix4() },
        uCameraFar: { value: camera.far },
        uTime: { value: 0 },
        uCloudBase: { value: CLOUDS.altitude.base },
        uCloudTop: { value: CLOUDS.altitude.top },
        uDrawDist: { value: CLOUDS.drawDistance },
        uNoiseScale: { value: CLOUDS.noiseScale },
        uWindDir: { value: new THREE.Vector3(...CLOUDS.windDir).normalize() },
        uWindSpeed: { value: CLOUDS.windSpeed },
        uCoverage: { value: 0.5 },
        uDensity: { value: 0.05 },
        uEdgeSoftness: { value: 0.35 },
        uNearFade: { value: CLOUDS.nearFade },
        uWispStrength: { value: 0 },
        uWispScale: { value: CLOUDS.wisp.scale },
        uWispDrift: { value: CLOUDS.wisp.drift },
        uWispFade: { value: new THREE.Vector2(CLOUDS.wisp.fadeStart, CLOUDS.wisp.fadeEnd) },
        uAlbedo: { value: new THREE.Vector3(1, 1, 1) },
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
        uSunColor: { value: new THREE.Vector3(1, 1, 1) },
        uSunScatter: { value: 1.0 },
        uSkyTop: { value: new THREE.Vector3(1, 1, 1) },
        uSkyBottom: { value: new THREE.Vector3(0.3, 0.3, 0.3) },
        uAmbient: { value: 1.0 },
        uEffectBrightness: { value: 1.0 },
        uFogColor: { value: new THREE.Vector3(0.5, 0.5, 0.5) },
        uFogNear: { value: 0 },
        uFogFar: { value: CLOUDS.drawDistance },
      },
      vertexShader: CLOUD_VERT,
      fragmentShader: cloudRaymarchFrag,
      depthTest: false,
      depthWrite: false,
    });

    this._compositeMat = new THREE.ShaderMaterial({
      name: 'CloudComposite',
      uniforms: {
        tDiffuse: { value: null },
        uClouds: { value: null },
        uCloudRes: { value: new THREE.Vector2(1, 1) },
      },
      vertexShader: CLOUD_VERT,
      fragmentShader: cloudCompositeFrag,
      depthTest: false,
      depthWrite: false,
    });

    // ── Separable depth-aware blur ────────────────────────────────────────────
    // Two extra targets and two extra passes, run between the raymarch and the
    // composite. Deliberately NOT applied to `_curr` in place: that buffer is
    // banked as next frame's reprojection history, and blurring it would feed the
    // blur into itself every frame until the deck dissolved. The history stays
    // sharp; only the copy that reaches the screen is blurred.
    const bl = CLOUDS.blur;
    this._blurEnabled = !!bl.enabled;
    this._blurA = null;
    this._blurB = null;
    this._blurMat = null;
    if (this._blurEnabled) {
      this._blurA = makeTarget();  // horizontal pass output
      this._blurB = makeTarget();  // vertical pass output — what the composite reads
      this._blurMat = new THREE.ShaderMaterial({
        name: 'CloudBlur',
        defines: { CLOUD_BLUR_TAPS: `${Math.max(1, bl.taps | 0)}` },
        uniforms: {
          uClouds: { value: null },
          uSceneDepth: { value: null },
          uDirection: { value: new THREE.Vector2() },
          uSigma: { value: Math.max(1e-3, bl.sigma) },
          uDepthTol: { value: bl.depthTolerance },
        },
        vertexShader: CLOUD_VERT,
        fragmentShader: cloudBlurFrag,
        depthTest: false,
        depthWrite: false,
      });
    }

    this._fsq = new FullScreenQuad(this._raymarchMat);
  }

  // ── Per-scenario configuration ────────────────────────────────────────────
  //
  // Called from App.applyLighting with the active LIGHTING entry and the HDR's
  // sky tint. Sets the deck's look (coverage/density/altitude/albedo), the sun
  // (direction/colour/scatter, shared with the scenario's directional light),
  // the hemispheric ambient (matching particles), and the scene fog.
  configure(lcfg, skyTint) {
    const cc = lcfg && lcfg.clouds;
    // Kept for setCoverage(), which re-derives the coverage-dependent uniforms
    // from this condition without re-running the whole configure.
    this._clouds = cc || null;
    this._sunOn = !!(lcfg && lcfg.sun && lcfg.sun.enabled);
    this.enabled = CLOUDS.enabled && this._userEnabled && !!cc && (cc.coverage ?? 0) > 0;
    // A new look (coverage/altitude/lighting) has nothing to do with the banked
    // history — drop it so the change doesn't blend in over several frames.
    this.resetHistory();
    if (!cc) {
      // No deck this condition → no shadow.
      this.shadowUniforms.uCloudShadowStrength.value = 0;
      return;
    }

    const u = this._raymarchMat.uniforms;

    const alt = cc.altitude || CLOUDS.altitude;
    u.uCloudBase.value = alt.base;
    u.uCloudTop.value = alt.top;
    u.uNoiseScale.value = cc.noiseScale ?? CLOUDS.noiseScale;
    u.uCoverage.value = cc.coverage;
    u.uDensity.value = cc.density;
    u.uEdgeSoftness.value = cc.edgeSoftness ?? 0.35;

    // Fine wisps. Scale is absolute metres — deliberately NOT derived from
    // noiseScale — so retuning the broad masses never resizes the detail.
    const wisp = CLOUDS.wisp;
    u.uWispStrength.value = wisp.enabled ? wisp.strength * (cc.wispStrength ?? 1.0) : 0.0;
    u.uWispScale.value = Math.max(1, cc.wispScale ?? wisp.scale);
    u.uWispDrift.value = cc.wispDrift ?? wisp.drift;
    // Guard fadeEnd > fadeStart so the shader's smoothstep can't divide by zero.
    u.uWispFade.value.set(wisp.fadeStart, Math.max(wisp.fadeEnd, wisp.fadeStart + 1));

    // Albedo (sRGB hex → linear).
    this._col.set(cc.albedo ?? 0xffffff);
    u.uAlbedo.value.set(this._col.r, this._col.g, this._col.b);

    // Sun — direction/colour come from the scenario's directional light so the
    // deck is lit consistently with the rest of the scene. `direction` is the
    // light-travel direction, so the toward-sun vector is its negation.
    const sun = lcfg.sun;
    const sunOn = this._sunOn;
    // Per-scenario sun response × the global master (CLOUDS.sunStrength). Also
    // gates the shader's sun sample, so 0 skips it entirely.
    u.uSunScatter.value = sunOn ? (cc.sunScatter ?? 1.0) * CLOUDS.sunStrength : 0.0;
    if (sun) {
      this._sunDir.set(sun.direction[0], sun.direction[1], sun.direction[2]).normalize().negate();
      u.uSunDir.value.copy(this._sunDir);
      const gain = sun.intensity ?? 1.0;
      this._col.set(sun.color ?? 0xffffff);
      u.uSunColor.value.set(this._col.r * gain, this._col.g * gain, this._col.b * gain);
    }

    // Hemispheric sky-tint ambient (identical inputs to ParticleSystem).
    const top = skyTint?.top ?? [1, 1, 1];
    const bot = skyTint?.bottom ?? [0.3, 0.3, 0.3];
    u.uSkyTop.value.set(top[0], top[1], top[2]);
    u.uSkyBottom.value.set(bot[0], bot[1], bot[2]);
    u.uAmbient.value = cc.ambient ?? 1.0;
    u.uEffectBrightness.value = lcfg.effectBrightness ?? 1.0;

    // Scene fog (sRGB hex → linear, to match the HDR-space beauty pass).
    if (lcfg.fog) {
      this._col.set(lcfg.fog.color);
      u.uFogColor.value.set(this._col.r, this._col.g, this._col.b);
      u.uFogNear.value = lcfg.fog.near;
      u.uFogFar.value = lcfg.fog.far;
    }

    // Mirror the resolved shape into the terrain cloud-shadow uniforms so the
    // ground shadow matches the deck overhead. Strength is gated by the deck
    // being active AND the sun being on (no sun → no cast), scaled by the
    // per-condition response and the global master.
    const sh = this.shadowUniforms;
    const shadowOn = CLOUDS.shadow.enabled && this.enabled && sunOn;
    sh.uCloudShadowStrength.value = shadowOn
      ? CLOUDS.shadow.strength * (cc.shadowStrength ?? 1.0)
      : 0.0;
    // Bias the shadow's coverage threshold only (visible clouds keep cc.coverage)
    // so shadows can be fattened/thinned independently of the deck.
    sh.uCloudShadowCoverage.value = Math.min(1, Math.max(0,
      cc.coverage + (CLOUDS.shadow.coverageBias ?? 0)));
    sh.uCloudShadowDensity.value = cc.density;
    sh.uCloudShadowEdge.value = cc.edgeSoftness ?? 0.35;
    sh.uCloudShadowNoiseScale.value = cc.noiseScale ?? CLOUDS.noiseScale;
    sh.uCloudShadowBase.value = alt.base;
    sh.uCloudShadowTop.value = alt.top;
    if (sun) sh.uCloudShadowSunDir.value.copy(this._sunDir);
  }

  /**
   * Apply a CLOUDS.quality preset (the Low/Medium/High graphics setting) live.
   * Everything here is pure sampling cost — none of it touches the deck's look —
   * so it can be switched mid-flight without a reconfigure:
   *
   *   • maxSteps/octaves are shader #defines, so they need a program rebuild.
   *     Three's cache key includes define *values*, so flipping them and setting
   *     `needsUpdate` compiles a new program on the next render (a one-frame hitch)
   *     rather than handing back the old one.
   *   • blurSigma is a plain uniform write.
   *   • resolutionScale reallocates the low-res targets, which drops the temporal
   *     history (setSize does that) since the old buffer's texels no longer line up.
   */
  setQuality(q) {
    const steps = `${Math.max(1, q.maxSteps | 0)}`;
    const octaves = `${Math.max(1, q.octaves | 0)}`;
    const defines = this._raymarchMat.defines;
    if (defines.CLOUD_STEPS !== steps || defines.CLOUD_OCTAVES !== octaves) {
      defines.CLOUD_STEPS = steps;
      defines.CLOUD_OCTAVES = octaves;
      this._raymarchMat.needsUpdate = true;
    }

    if (this._blurMat) this._blurMat.uniforms.uSigma.value = Math.max(1e-3, q.blurSigma);

    const scale = Math.max(0.05, q.resolutionScale);
    if (scale !== this._resolutionScale) {
      this._resolutionScale = scale;
      this.setSize(this._fullWidth, this._fullHeight);
    }
  }

  /**
   * Live coverage override — the start screen's cloud slider. Rewrites only the
   * coverage-derived state (deck uniform, pass enablement, terrain shadow) so a
   * drag doesn't re-run configure()'s full per-condition setup. The temporal
   * history is deliberately kept: it blends the deck across the change instead
   * of popping. No-op until a condition with a deck has been configured.
   */
  setCoverage(coverage) {
    const cc = this._clouds;
    if (!cc) return;
    const c = Math.min(1, Math.max(0, coverage));

    this._raymarchMat.uniforms.uCoverage.value = c;
    this._syncEnablement();
    this.shadowUniforms.uCloudShadowCoverage.value = Math.min(1, Math.max(0,
      c + (CLOUDS.shadow.coverageBias ?? 0)));
  }

  /**
   * The graphics setting's Off switch. Disabling skips the pass outright (and
   * zeroes the terrain cast, which early-outs on strength 0), so the deck costs
   * nothing; the configured look is kept so re-enabling needs no reconfigure.
   */
  setEnabled(on) {
    const want = !!on;
    if (want === this._userEnabled) return;
    this._userEnabled = want;
    this._syncEnablement();
    // Nothing was banked while the pass was skipped — don't reproject into it.
    if (want) this.resetHistory();
  }

  // Re-derive pass enablement and the terrain shadow strength from the current
  // coverage. Shared by setCoverage()/setEnabled(); configure() does its own,
  // since it is already writing the whole shadow block.
  _syncEnablement() {
    const cc = this._clouds;
    const c = this._raymarchMat.uniforms.uCoverage.value;
    this.enabled = CLOUDS.enabled && this._userEnabled && !!cc && c > 0;
    this.shadowUniforms.uCloudShadowStrength.value =
      (CLOUDS.shadow.enabled && this.enabled && this._sunOn)
        ? CLOUDS.shadow.strength * (cc.shadowStrength ?? 1.0)
        : 0.0;
  }

  // ── Per-frame ─────────────────────────────────────────────────────────────

  update(dt) {
    this._raymarchMat.uniforms.uTime.value += dt;
    // Keep the terrain cloud-shadow on the same clock so its drift matches.
    this.shadowUniforms.uCloudShadowTime.value = this._raymarchMat.uniforms.uTime.value;
  }

  // Discard the temporal history so the next frame starts fresh. Call on hard
  // camera cuts (mode switches, respawns) to avoid a few frames of ghosting.
  resetHistory() {
    this._historyValid = false;
  }

  setSize(width, height) {
    this._fullWidth = width;
    this._fullHeight = height;
    const w = Math.max(1, Math.round(width * this._resolutionScale));
    const h = Math.max(1, Math.round(height * this._resolutionScale));
    this._prev.setSize(w, h);
    this._curr.setSize(w, h);
    if (this._blurEnabled) {
      this._blurA.setSize(w, h);
      this._blurB.setSize(w, h);
    }
    this._compositeMat.uniforms.uCloudRes.value.set(w, h);
    // Reprojection UVs no longer line up with the old buffers.
    this.resetHistory();
  }

  render(renderer, writeBuffer, readBuffer) {
    const cam = this.camera;
    const u = this._raymarchMat.uniforms;

    // Scene depth + reconstruction matrices for this frame's camera pose.
    u.uSceneDepth.value = readBuffer.depthTexture;
    u.uCameraPos.value.copy(cam.position);
    u.uInvProjection.value.copy(cam.projectionMatrixInverse);
    u.uInvView.value.copy(cam.matrixWorld);
    u.uViewMatrix.value.copy(cam.matrixWorldInverse);
    u.uCameraFar.value = cam.far;

    // Temporal accumulation: feed the previous frame's buffer + camera transform
    // and advance the per-frame jitter. On the first valid frame there's no
    // history, so the shader falls back to the raw sample.
    u.uPrevHistory.value = this._prev.texture;
    u.uPrevViewProj.value.copy(this._prevViewProj);
    u.uHistoryValid.value = this._historyValid ? 1 : 0;
    u.uJitter.value = this._jitter;

    // Raymarch the deck at reduced resolution, accumulating into `_curr`. Binding
    // a different target here resolves the MSAA readBuffer's colour + depth into
    // its textures first.
    this._fsq.material = this._raymarchMat;
    renderer.setRenderTarget(this._curr);
    this._fsq.render(renderer);

    // Blur a copy of the accumulation, horizontally then vertically. Both passes
    // read the same depth the march clipped against, so their taps stop at the
    // occlusion silhouette instead of dragging cloud across it. `_curr` itself is
    // left untouched — it is next frame's history.
    let cloudTex = this._curr.texture;
    if (this._blurEnabled) {
      const bu = this._blurMat.uniforms;
      bu.uSceneDepth.value = readBuffer.depthTexture;
      const w = Math.max(1, this._curr.width);
      const h = Math.max(1, this._curr.height);

      bu.uClouds.value = this._curr.texture;
      bu.uDirection.value.set(1 / w, 0);
      this._fsq.material = this._blurMat;
      renderer.setRenderTarget(this._blurA);
      this._fsq.render(renderer);

      bu.uClouds.value = this._blurA.texture;
      bu.uDirection.value.set(0, 1 / h);
      renderer.setRenderTarget(this._blurB);
      this._fsq.render(renderer);

      cloudTex = this._blurB.texture;
    }

    // Composite over the beauty pass at full resolution.
    this._compositeMat.uniforms.tDiffuse.value = readBuffer.texture;
    this._compositeMat.uniforms.uClouds.value = cloudTex;
    this._fsq.material = this._compositeMat;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    this._fsq.render(renderer);

    // Bank this frame: remember the camera's world→clip for next frame's
    // reprojection, ping-pong the buffers, and advance the jitter along the
    // golden-ratio sequence (kept in [0,1) so it never loses float precision).
    this._prevViewProj.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    const swap = this._prev; this._prev = this._curr; this._curr = swap;
    this._historyValid = true;
    this._jitter = (this._jitter + 0.61803398875) % 1;
  }

  dispose() {
    this._prev.dispose();
    this._curr.dispose();
    if (this._blurA) this._blurA.dispose();
    if (this._blurB) this._blurB.dispose();
    this._noise.dispose();
    this._blueNoise.dispose();
    this._raymarchMat.dispose();
    this._compositeMat.dispose();
    if (this._blurMat) this._blurMat.dispose();
    this._fsq.dispose();
  }

  // ── Noise texture ─────────────────────────────────────────────────────────
  //
  // A 256×256 value-noise texture packed for the shader's 3D-from-2D `noise()`
  // (Íñigo Quílez's trick): R is per-texel value noise; G is R shifted by the
  // per-z-slice uv offset (37, 239) so consecutive z-slices stay continuous when
  // the shader mixes the .yx channels. Seeded so the deck looks identical across
  // reloads. Built once and reused for the lifetime of the pass.
  static buildNoiseTexture() {
    const S = 256;
    const OX = 37, OY = 239;
    const rng = mulberry32(0xc10d5eed);
    const R = new Uint8Array(S * S);
    for (let i = 0; i < S * S; i++) R[i] = (rng() * 256) | 0;

    const data = new Uint8Array(S * S * 4);
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const idx = y * S + x;
        const gx = (x - OX + S) % S;
        const gy = (y - OY + S) % S;
        const o = idx * 4;
        data[o] = R[idx];               // R — value noise
        data[o + 1] = R[gy * S + gx];   // G — R shifted by the z-slice offset
        data[o + 2] = 0;
        data[o + 3] = 255;
      }
    }

    const tex = new THREE.DataTexture(data, S, S, THREE.RGBAFormat);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return tex;
  }

  // ── Blue-noise dither texture ─────────────────────────────────────────────
  //
  // A 64×64 tileable blue-noise mask generated with Ulichney's void-and-cluster
  // method, used to jitter each ray's start offset. Blue noise has no low-
  // frequency energy, so unlike white/gradient noise it doesn't survive the
  // low-res raymarch → bicubic upscale as visible bands or diagonal stripes — the
  // banding dissolves into fine, unstructured grain instead. Seeded so it's
  // identical across reloads; built once. Nearest-sampled so each ray gets a
  // crisp value. Runs at construction (~one-time tens of ms).
  static buildBlueNoiseTexture() {
    const N = 64;
    const SIZE = N * N;
    const rng = mulberry32(0xb1e0f5ee);

    // Toroidal Gaussian energy field with a truncated kernel (weights beyond the
    // radius are negligible), so add/remove is O(kernel), not O(SIZE).
    const SIGMA = 1.9;
    const RAD = 6;
    const K = 2 * RAD + 1;
    const kernel = new Float32Array(K * K);
    for (let dy = -RAD; dy <= RAD; dy++) {
      for (let dx = -RAD; dx <= RAD; dx++) {
        kernel[(dy + RAD) * K + (dx + RAD)] = Math.exp(-(dx * dx + dy * dy) / (2 * SIGMA * SIGMA));
      }
    }

    const binary = new Uint8Array(SIZE);
    const energy = new Float32Array(SIZE);
    const splat = (idx, sign) => {
      const x = idx % N, y = (idx / N) | 0;
      for (let dy = -RAD; dy <= RAD; dy++) {
        const yy = (y + dy + N) % N;
        for (let dx = -RAD; dx <= RAD; dx++) {
          const xx = (x + dx + N) % N;
          energy[yy * N + xx] += sign * kernel[(dy + RAD) * K + (dx + RAD)];
        }
      }
    };
    const tightestCluster = () => {
      let best = -1, bestE = -Infinity;
      for (let i = 0; i < SIZE; i++) if (binary[i] && energy[i] > bestE) { bestE = energy[i]; best = i; }
      return best;
    };
    const largestVoid = () => {
      let best = -1, bestE = Infinity;
      for (let i = 0; i < SIZE; i++) if (!binary[i] && energy[i] < bestE) { bestE = energy[i]; best = i; }
      return best;
    };

    // Seed ~10% ones, then homogenize by swapping the tightest cluster into the
    // largest void until the two coincide (the prototype pattern).
    const ones = Math.round(SIZE * 0.1);
    for (let placed = 0; placed < ones;) {
      const idx = (rng() * SIZE) | 0;
      if (!binary[idx]) { binary[idx] = 1; splat(idx, +1); placed++; }
    }
    for (let iter = 0; iter < SIZE; iter++) {
      const c = tightestCluster();
      binary[c] = 0; splat(c, -1);
      const v = largestVoid();
      binary[v] = 1; splat(v, +1);
      if (v === c) break;
    }

    const rank = new Int32Array(SIZE).fill(-1);
    const proto = binary.slice();
    const protoEnergy = energy.slice();

    // Phase 1 — remove ones tightest-first: ranks [0, ones-1].
    for (let r = ones - 1; r >= 0; r--) {
      const c = tightestCluster();
      rank[c] = r; binary[c] = 0; splat(c, -1);
    }
    // Phase 2 — refill the prototype's voids largest-first: ranks [ones, SIZE-1].
    binary.set(proto); energy.set(protoEnergy);
    for (let r = ones; r < SIZE; r++) {
      const v = largestVoid();
      rank[v] = r; binary[v] = 1; splat(v, +1);
    }

    // Rank → uniform [0,1] threshold value.
    const data = new Uint8Array(SIZE);
    const inv = 255 / (SIZE - 1);
    for (let i = 0; i < SIZE; i++) data[i] = Math.round(rank[i] * inv);

    const tex = new THREE.DataTexture(data, N, N, THREE.RedFormat);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.NearestFilter;
    tex.magFilter = THREE.NearestFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return tex;
  }
}

// Small seeded PRNG so the baked noise is deterministic across sessions.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
